import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

/**
 * Professional content renderer for parsed markdown sections
 * Supports headings, paragraphs, and collapsible code blocks
 */
export function StructuredContent({ sections }) {
  if (!sections || sections.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400">
        No content available
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section, i) => (
        <ContentSection key={i} section={section} />
      ))}
    </div>
  );
}

function ContentSection({ section }) {
  if (section.type === 'heading') {
    return <HeadingSection section={section} />;
  }
  
  if (section.type === 'paragraph') {
    return <ParagraphSection section={section} />;
  }
  
  if (section.type === 'code') {
    return <CodeSection section={section} />;
  }
  
  if (section.type === 'list') {
    return <ListSection section={section} />;
  }
  
  if (section.type === 'table') {
    return <TableSection section={section} />;
  }
  
  return null;
}

function HeadingSection({ section }) {
  const { level, content } = section;
  
  const sizes = {
    1: 'text-3xl font-bold mb-4 mt-8 text-gray-900 dark:text-white',
    2: 'text-2xl font-bold mb-3 mt-7 text-gray-900 dark:text-white',
    3: 'text-xl font-semibold mb-2 mt-6 text-gray-800 dark:text-gray-100',
    4: 'text-lg font-semibold mb-2 mt-5 text-gray-800 dark:text-gray-100',
    5: 'text-base font-semibold mb-2 mt-4 text-gray-700 dark:text-gray-200',
    6: 'text-sm font-semibold mb-2 mt-4 text-gray-700 dark:text-gray-200'
  };
  
  // Parse inline markdown for headings (remove ** __ * _ ` formatting)
  const cleanContent = content
    .replace(/\*\*(.+?)\*\*/g, '$1')  // Remove **bold**
    .replace(/__(.+?)__/g, '$1')      // Remove __bold__
    .replace(/\*(.+?)\*/g, '$1')      // Remove *italic*
    .replace(/_(.+?)_/g, '$1')        // Remove _italic_
    .replace(/`(.+?)`/g, '$1');       // Remove `code`
  
  const HeadingTag = `h${level}`;
  const className = sizes[level] || sizes[6];
  
  return React.createElement(HeadingTag, { className }, cleanContent);
}

function ParagraphSection({ section }) {
  // Parse inline markdown formatting
  const parseInlineMarkdown = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Regular expressions for inline markdown
    const patterns = [
      { regex: /\*\*(.+?)\*\*/g, component: 'strong' },      // **bold**
      { regex: /\*(.+?)\*/g, component: 'em' },              // *italic*
      { regex: /__(.+?)__/g, component: 'strong' },          // __bold__
      { regex: /_(.+?)_/g, component: 'em' },                // _italic_
      { regex: /`(.+?)`/g, component: 'code' },              // `code`
      { regex: /\[(.+?)\]\((.+?)\)/g, component: 'link' },   // [text](url)
    ];
    
    // Find all matches
    const allMatches = [];
    patterns.forEach(({ regex, component }) => {
      const matches = [...text.matchAll(regex)];
      matches.forEach(match => {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[1],
          url: match[2], // for links
          component
        });
      });
    });
    
    // Sort by position
    allMatches.sort((a, b) => a.start - b.start);
    
    // Build elements
    let key = 0;
    allMatches.forEach((match) => {
      // Add text before match
      if (match.start > currentIndex) {
        parts.push(text.substring(currentIndex, match.start));
      }
      
      // Add formatted element
      if (match.component === 'strong') {
        parts.push(<strong key={key++} className="font-semibold">{match.text}</strong>);
      } else if (match.component === 'em') {
        parts.push(<em key={key++} className="italic">{match.text}</em>);
      } else if (match.component === 'code') {
        parts.push(
          <code key={key++} className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-bg-tertiary rounded text-sm font-mono text-orange-600 dark:text-orange-400">
            {match.text}
          </code>
        );
      } else if (match.component === 'link') {
        parts.push(
          <a key={key++} href={match.url} className="text-orange-600 dark:text-orange-500 hover:underline" target="_blank" rel="noopener noreferrer">
            {match.text}
          </a>
        );
      }
      
      currentIndex = match.end;
    });
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  return (
    <p className="text-base leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">
      {parseInlineMarkdown(section.content)}
    </p>
  );
}

function ListSection({ section }) {
  // Helper to parse inline markdown
  const parseInlineMarkdown = (text) => {
    const parts = [];
    let currentText = text;
    
    // Bold (**text** or __text__)
    currentText = currentText.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    currentText = currentText.replace(/__(.+?)__/g, '<strong class="font-semibold">$1</strong>');
    
    // Italic (*text* or _text_)
    currentText = currentText.replace(/\*([^\*]+?)\*/g, '<em class="italic">$1</em>');
    currentText = currentText.replace(/_([^_]+?)_/g, '<em class="italic">$1</em>');
    
    // Code (`text`)
    currentText = currentText.replace(/`(.+?)`/g, '<code class="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-bg-tertiary rounded text-sm font-mono text-orange-600 dark:text-orange-400">$1</code>');
    
    return <span dangerouslySetInnerHTML={{ __html: currentText }} />;
  };
  
  return (
    <ul className="space-y-2 my-4 ml-6 list-disc text-gray-700 dark:text-gray-300">
      {section.items.map((item, idx) => (
        <li key={idx} className="leading-relaxed" style={{ marginLeft: `${item.indent * 1.5}rem` }}>
          {parseInlineMarkdown(item.text)}
        </li>
      ))}
    </ul>
  );
}

function TableSection({ section }) {
  // Parse markdown table
  const lines = section.content.split('\n').filter(line => line.trim());
  if (lines.length < 2) return null;
  
  const headers = lines[0].split('|').filter(cell => cell.trim()).map(h => h.trim());
  const rows = lines.slice(2).map(line => 
    line.split('|').filter(cell => cell.trim()).map(c => c.trim())
  );
  
  return (
    <div className="my-6 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2e2e2d] border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
        <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
          <tr>
            {headers.map((header, idx) => (
              <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-dark-bg divide-y divide-gray-200 dark:divide-[#2e2e2d]">
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary">
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeSection({ section }) {
  const [isExpanded, setIsExpanded] = useState(true);  // Default to expanded
  const [copied, setCopied] = useState(false);
  
  const { language, content } = section;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="my-6 rounded-lg overflow-hidden border border-gray-200 dark:border-[#2e2e2d]">
      {/* Header */}
      <div className="bg-gray-100 dark:bg-dark-bg-secondary px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="font-mono text-xs uppercase tracking-wide">
              {language || 'code'}
            </span>
          </button>
          {!isExpanded && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Click to expand
            </span>
          )}
        </div>
        
        {isExpanded && (
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-white dark:bg-dark-bg-tertiary rounded-md hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copy
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Code Content - Collapsible */}
      {isExpanded && (
        <pre className="bg-gray-50 dark:bg-dark-bg p-4 overflow-x-auto">
          <code className={`language-${language} text-sm text-gray-800 dark:text-gray-200 font-mono`}>
            {content}
          </code>
        </pre>
      )}
    </div>
  );
}
